<script lang="ts" setup>
import PaginationTable from '@/components/tables/pagination-table/logic/PaginationTable.vue';
import type {
  PaginationQueryAction,
  PaginationQueryState,
  PaginationTableColumn,
} from '@/components/tables/pagination-table/types';
import { useElementSize } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useAllInvoicesTableColumns } from './all-invoices-table-columns';
import AllInvoicesTableRows from './all-invoices-table-rows.vue';
import type { AllInvoicesTableInvoice } from './types';

defineProps<{
  rows: AllInvoicesTableInvoice[];
  totalRows: number;
  filters?: PaginationQueryState;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'row-click', invoice: AllInvoicesTableInvoice): void;
  (e: 'navigate-to-project', invoice: AllInvoicesTableInvoice): void;
  (e: 'query-change', action: PaginationQueryAction): void;
}>();

const tableRef = ref<HTMLElement | null>(null);
const { width } = useElementSize(tableRef, { width: 9999, height: 0 });

// Breakpoints are based on container width (sum of column widths at each stage).
// All columns: ~1448px. Buffer of ~50px triggers removal before overflow.
const HIDE_INVOICE_DATE_BELOW = 1500;
const HIDE_VAT_BELOW = 1380;
const HIDE_APPROVER_BELOW = 1260;
const HIDE_INVOICE_NUMBER_BELOW = 1160;

const allColumns: PaginationTableColumn<AllInvoicesTableInvoice>[] = [
  ...useAllInvoicesTableColumns(),
  { key: '_actions', sortable: false, columnWidth: '48px' },
];

const columns = computed(() =>
  allColumns.filter((col) => {
    if (col.key === 'invoiceDate' && width.value < HIDE_INVOICE_DATE_BELOW)
      return false;
    if (col.key === 'vat' && width.value < HIDE_VAT_BELOW) return false;
    if (col.key === 'approver' && width.value < HIDE_APPROVER_BELOW)
      return false;
    if (col.key === 'invoiceNumber' && width.value < HIDE_INVOICE_NUMBER_BELOW)
      return false;
    return true;
  }),
);

function onQueryChange(action: PaginationQueryAction) {
  emit('query-change', action);
}
</script>

<template>
  <div class="invoice-table show-row-colors" ref="tableRef">
    <PaginationTable
      :columns="columns"
      :rows="rows"
      :total-items="totalRows"
      :row-class="(row) => row.status"
      :query="filters"
      :is-loading="isLoading"
      @query-change="onQueryChange"
      @row-click="emit('row-click', $event)"
    >
      <template #cell="{ row, column, value }">
        <AllInvoicesTableRows
          :row="row"
          :column="column"
          :value="value"
          @navigate-to-project="emit('navigate-to-project', row)"
        />
      </template>
    </PaginationTable>
  </div>
</template>

<style scoped lang="scss">
.invoice-table {
  :deep(table tbody) {
    tr {
      cursor: pointer;
    }
    tr td:last-child {
      vertical-align: middle;
    }
    tr.incoming {
      background-color: #90caf9;
      &:hover {
        background-color: color-mix(in srgb, #90caf9 90%, black);
      }
    }
    tr.undercertificate {
      background-color: var(--invoice-undercertificate);
      &:hover {
        background-color: color-mix(
          in srgb,
          var(--invoice-undercertificate) 90%,
          black
        );
      }
    }
    tr.parked {
      background-color: var(--invoice-parked);
      &:hover {
        background-color: color-mix(in srgb, var(--invoice-parked) 90%, black);
      }
    }
    tr.disputed {
      background-color: var(--invoice-disputed);
      &:hover {
        background-color: color-mix(
          in srgb,
          var(--invoice-disputed) 90%,
          black
        );
      }
    }
    tr.approved {
      background-color: var(--invoice-approved);
      &:hover {
        background-color: color-mix(
          in srgb,
          var(--invoice-approved) 90%,
          black
        );
      }
    }
    tr.paid {
      background-color: var(--invoice-paid);
      &:hover {
        background-color: color-mix(in srgb, var(--invoice-paid) 90%, black);
      }
    }
    tr.credit {
      background-color: var(--invoice-credit);
      &:hover {
        background-color: color-mix(in srgb, var(--invoice-credit) 90%, black);
      }
    }
    tr.voided {
      background-color: var(--invoice-voided);
      &:hover {
        background-color: color-mix(in srgb, var(--invoice-voided) 90%, black);
      }
    }
    tr.verified-debt {
      background-color: var(--invoice-verified-debt);
      &:hover {
        background-color: color-mix(
          in srgb,
          var(--invoice-verified-debt) 90%,
          black
        );
      }
    }
  }
}
</style>
